/**
 * CustomFilterSelect - Web component for filterable dropdown selection
 * Supports both tag and metafield filtering with search functionality
 */
class CustomFilterSelect extends HTMLElement {
	constructor() {
		super();
		this.initializeElements();
		this.initializeState();
		this.bindMethods();
		
		if (this.isValidComponent()) {
			this.init();
		}
	}

	/**
	 * Initialize DOM elements
	 */
	initializeElements() {
		this.searchInput = this.querySelector('[data-custom-filter-select-search]');
		this.options = this.querySelector('[data-custom-filter-select-dropdown]');
		this.optionsList = this.querySelectorAll('[data-custom-filter-select-option]');
		this.arrow = this.querySelector('[data-custom-filter-select-arrow]');
		this.deleteBtn = this.querySelector('[data-custom-filter-select-delete-btn]');
		this.wrapper = this.closest("tag-filter-tabs");
	}

	/**
	 * Initialize component state
	 */
	initializeState() {
		this.isOpen = false;
		this.selectedOption = null;
		this._cachedFilterInfo = null;
		this.filterType = this.getFilterType();
		
		if (this.filterType === 'metafield') {
			this.currentMetafieldKey = this.getCurrentMetafieldKey();
		}
	}

	/**
	 * Bind event handler methods to maintain correct context
	 */
	bindMethods() {
		this.handleSearchBound = this.handleSearch.bind(this);
		this.handleSearchKeydownBound = this.handleSearchKeydown.bind(this);
		this.toggleBound = this.toggle.bind(this);
		this.selectOptionBound = this.selectOption.bind(this);
		this.handleOutsideClickBound = this.handleOutsideClick.bind(this);
		this.handleDeleteClickBound = this.handleDeleteClick.bind(this);
	}

	/**
	 * Check if component has required elements
	 */
	isValidComponent() {
		return this.searchInput && this.options;
	}
	
	/**
	 * Initialize the component
	 */
	init() {
		this.setupOptions();
		this.setupEventListeners();
		this.initializeDisplay();
		this.scheduleInitialValueSetup();
	}

	/**
	 * Setup options array and initial visibility
	 */
	setupOptions() {
		this.allOptions = Array.from(this.optionsList);
		this.showAllOptions();
	}

	/**
	 * Setup all event listeners
	 */
	setupEventListeners() {
		this.addSearchEventListeners();
		this.addControlEventListeners();
		this.addOptionEventListeners();
		this.addDocumentEventListeners();
	}

	/**
	 * Add search input event listeners
	 */
	addSearchEventListeners() {
		this.searchInput.addEventListener('input', this.handleSearchBound);
		this.searchInput.addEventListener('keydown', this.handleSearchKeydownBound);
		this.searchInput.addEventListener('click', this.toggleBound);
	}

	/**
	 * Add control button event listeners
	 */
	addControlEventListeners() {
		if (this.arrow) {
			this.arrow.addEventListener('click', this.toggleBound);
		}
		
		if (this.deleteBtn) {
			this.deleteBtn.addEventListener('click', this.handleDeleteClickBound);
		}
	}

	/**
	 * Add option click event listeners
	 */
	addOptionEventListeners() {
		this.optionsList.forEach(option => {
			option.addEventListener('click', this.selectOptionBound);
		});
	}

	/**
	 * Add document-level event listeners
	 */
	addDocumentEventListeners() {
		document.addEventListener('click', this.handleOutsideClickBound);
	}

	/**
	 * Initialize display state
	 */
	initializeDisplay() {
		this.updateActionDisplay();
	}

	/**
	 * Schedule initialization of selected values after DOM is ready
	 */
	scheduleInitialValueSetup() {
		setTimeout(() => {
			this.initializeSelectedValue();
		}, 100);
	}

	handleOutsideClick(e) {
		if (!this.contains(e.target)) {
			this.close();
		}
	}

	/**
	 * Cleanup when component is removed from DOM
	 */
	disconnectedCallback() {
		this.removeAllEventListeners();
	}

	/**
	 * Remove all event listeners to prevent memory leaks
	 */
	removeAllEventListeners() {
		this.removeSearchEventListeners();
		this.removeControlEventListeners();
		this.removeOptionEventListeners();
		this.removeDocumentEventListeners();
	}

	/**
	 * Remove search input event listeners
	 */
	removeSearchEventListeners() {
		if (this.searchInput) {
			this.searchInput.removeEventListener('input', this.handleSearchBound);
			this.searchInput.removeEventListener('keydown', this.handleSearchKeydownBound);
			this.searchInput.removeEventListener('click', this.toggleBound);
		}
	}

	/**
	 * Remove control button event listeners
	 */
	removeControlEventListeners() {
		if (this.arrow) {
			this.arrow.removeEventListener('click', this.toggleBound);
		}
		
		if (this.deleteBtn) {
			this.deleteBtn.removeEventListener('click', this.handleDeleteClickBound);
		}
	}

	/**
	 * Remove option click event listeners
	 */
	removeOptionEventListeners() {
		this.optionsList.forEach(option => {
			option.removeEventListener('click', this.selectOptionBound);
		});
	}

	/**
	 * Remove document-level event listeners
	 */
	removeDocumentEventListeners() {
		document.removeEventListener('click', this.handleOutsideClickBound);
	}
	
	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			// If there's a selected option, clear it first, then open
			if (this.selectedOption || this.hasActiveFilter()) {
				this.clearFilterAndOpen();
			} else {
				this.open();
			}
		}
	}

	/**
	 * Clear filter from URL and open dropdown
	 */
	clearFilterAndOpen() {
		// Send event to parent for centralized URL clearing
		const clearFilterEvent = new CustomEvent('clear-filter', {
			detail: {
				target: this,
				shouldOpen: true
			}
		});
		this.wrapper.dispatchEvent(clearFilterEvent);
	}
	
	open() {
		this.isOpen = true;
		this.searchInput.setAttribute('aria-expanded', 'true');
		this.options.setAttribute('aria-hidden', 'false');
		this.options.classList.add('visible');
		if (this.arrow) {
			this.arrow.classList.add('rotated');
		}
	}
	
	close() {
		this.isOpen = false;
		this.searchInput.setAttribute('aria-expanded', 'false');
		this.options.setAttribute('aria-hidden', 'true');
		this.options.classList.remove('visible');
		if (this.arrow) {
			this.arrow.classList.remove('rotated');
		}
		
		// Restore correct input value if current value doesn't match selected option
		const currentInputValue = this.searchInput.value.trim();
		const selectedOptionText = this.selectedOption ? this.selectedOption.textContent.trim() : '';
		
		// If input value doesn't match selected option, restore correct value
		if (currentInputValue !== selectedOptionText) {
			this.searchInput.value = selectedOptionText;
		}

		this.showAllOptions();
	}
	
	selectOption(e) {
		const option = e.currentTarget;
		const text = option.textContent.trim();
		
		// Don't select if this option is already selected
		if (this.selectedOption === option) {
			return;
		}
		
		// Update input value
		this.searchInput.value = text;
		this.selectedOption = option;
		
		// Remove previous selection
		this.optionsList.forEach(opt => opt.classList.remove('selected'));
		option.classList.add('selected');

		// Close dropdown
		this.close();
		
		// Clear search filter to show all options again
		this.showAllOptions();
		
		// Update arrow/delete button display
		this.updateActionDisplay();

		// Send event to parent for centralized URL handling
		const updateOptionEvent = new CustomEvent('update-option', {
			detail: {
				target: this,
				selectedOption: option
			}})
		this.wrapper.dispatchEvent(updateOptionEvent);

	
	
	}


	setLoadingState(isLoading) {
		const countContainer = document.getElementById('ProductCount');
		const countContainerDesktop = document.getElementById('ProductCountDesktop');
		const gridContainer = document.getElementById('ProductGridContainer');
		
		if (gridContainer) {
			gridContainer.querySelector('.collection')?.classList.toggle('loading', isLoading);
		}
		if (countContainer) {
			countContainer.classList.toggle('loading', isLoading);
		}
		if (countContainerDesktop) {
			countContainerDesktop.classList.toggle('loading', isLoading);
		}
	}

	getFilterType() {
		const dropdown = this.querySelector('[data-custom-filter-select-dropdown]');
		return dropdown?.getAttribute('data-filter-type') || 'tag';
	}

	
	handleSearch(e) {
		const searchTerm = e.target.value.toLowerCase().trim();
		
		// Auto-open dropdown when user starts typing (if not already open)
		if (!this.isOpen && searchTerm.length > 0) {
			this.open();
		}
		
		if (searchTerm === '') {
			this.showAllOptions();
		} else {
			this.filterOptions(searchTerm);
		}
	}
	
	handleSearchKeydown(e) {
		if (e.key === 'Enter') {
			e.preventDefault();
			this.handleEnterKey();
		} else if (e.key === 'Escape') {
			this.close();
			this.searchInput.blur();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			this.focusNextOption();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			this.focusPreviousOption();
		}
	}
	
	handleEnterKey() {
		const visibleOptions = this.allOptions.filter(option => 
			option.classList.contains('visible')
		);
		
		const regularOptions = visibleOptions.filter(option => 
			!option.classList.contains('custom-filter-select__option--fixed')
		);
		
		const fixedOptions = visibleOptions.filter(option => 
			option.classList.contains('custom-filter-select__option--fixed')
		);
		
		if (regularOptions.length === 1) {
			this.selectOption({ currentTarget: regularOptions[0] });
		}
		else if (regularOptions.length === 0 && fixedOptions.length === 1) {
			this.selectOption({ currentTarget: fixedOptions[0] });
		}
	}
	
	filterOptions(searchTerm) {
		if (searchTerm === 'saint') {
			this.allOptions.forEach(option => {
				const text = option.textContent.toLowerCase();
				const isFixedOption = option.classList.contains('custom-filter-select__option--fixed');
				const containsSaint = text.includes('saint');
				
				const shouldShow = isFixedOption || containsSaint;
				
				if (shouldShow) {
					option.classList.add('visible');
				} else {
					option.classList.remove('visible');
				}
			});
			return;
		}
		
		let actualSearchTerm = searchTerm;
		let filterOnlySaintOptions = false;
		
		if (searchTerm.startsWith('saint ')) {
			actualSearchTerm = searchTerm.substring(6); // Remove "saint "
			filterOnlySaintOptions = true;
		}
		
		this.allOptions.forEach(option => {
			const text = option.textContent.toLowerCase();
			const isFixedOption = option.classList.contains('custom-filter-select__option--fixed');
			const containsSaint = text.includes('saint');
			
			// If filtering after "saint ", only consider options that contain "saint"
			if (filterOnlySaintOptions && !containsSaint && !isFixedOption) {
				option.classList.remove('visible');
				return;
			}
			
			// Remove "saint" from text before filtering
			const filteredText = text.replace(/\bsaint\b/g, '').trim();
			
			// Split text into words and check if any word starts with the search term
			const words = filteredText.split(/\s+/);
			const matchesSearch = words.some(word => word.startsWith(actualSearchTerm));
			
			// Fixed options are always visible, regular options only if they match search
			const shouldShow = isFixedOption || matchesSearch;
			
			if (shouldShow) {
				option.classList.add('visible');
			} else {
				option.classList.remove('visible');
			}
		});
		
		// Ensure fixed options are always visible, even when no search results
		this.allOptions.forEach(option => {
			const isFixedOption = option.classList.contains('custom-filter-select__option--fixed');
			if (isFixedOption) {
				option.classList.add('visible');
			}
		});
	}
	
	showAllOptions() {
		this.allOptions.forEach(option => {
			option.classList.add('visible');
		});
	}
	
	focusNextOption() {
		const visibleOptions = this.allOptions.filter(option => 
			option.classList.contains('visible')
		);
		if (visibleOptions.length > 0) {
			visibleOptions[0].focus();
		}
	}
	
	focusPreviousOption() {
		const visibleOptions = this.allOptions.filter(option => 
			option.classList.contains('visible')
		);
		if (visibleOptions.length > 0) {
			visibleOptions[visibleOptions.length - 1].focus();
		}
	}


	handleDeleteClick(e) {
		if (e) e.stopPropagation();
		this.clearFilterFromUrl();
	}

	/**
	 * Clear filter from URL via delete button
	 */
	clearFilterFromUrl() {
		const clearFilterEvent = new CustomEvent('clear-filter', {
			detail: {
				target: this,
				shouldOpen: false
			}
		});
		this.wrapper.dispatchEvent(clearFilterEvent);
	}


	getCurrentMetafieldKey() {
		const firstMetafieldOption = this.querySelector('[data-metafield-filter-key]');
		if (firstMetafieldOption) {
			return firstMetafieldOption.getAttribute('data-metafield-filter-key');
		}
		
		return null;
	}

	updateActionDisplay() {
		const hasSelectedValue = this.selectedOption !== null || this.hasActiveFilter();
		
		if (this.arrow && this.deleteBtn) {
			if (hasSelectedValue) {
				this.arrow.classList.add('hidden');
				this.deleteBtn.classList.remove('hidden');
			} else {
				this.arrow.classList.remove('hidden');
				this.deleteBtn.classList.add('hidden');
			}
		}
	}

	getActiveFilterInfo() {
		if (this._cachedFilterInfo) {
			return this._cachedFilterInfo;
		}
		if (this.filterType === 'tag') {
			const pathname = new URL(document.location).pathname;
			const urlArray = pathname.split('/');
			const tagsFromUrl = urlArray[urlArray.length - 1];
			const collectionUrl = this.getAttribute('data-collection-url') || '';
			
			if (!tagsFromUrl || pathname === collectionUrl) {
				this._cachedFilterInfo = { hasFilter: false, option: null };
				return this._cachedFilterInfo;
			}
			
			const selectedOption = Array.from(this.optionsList).find(option => {
				const optionTag = option.getAttribute('data-filtering-tags');
				return optionTag && optionTag.toLowerCase().split(" ").join("-") === tagsFromUrl;
			});
			
			this._cachedFilterInfo = { hasFilter: !!selectedOption, option: selectedOption };
			return this._cachedFilterInfo;
		} else if (this.filterType === 'metafield') {
			const searchParams = new URLSearchParams(window.location.search);
			
			if (!this.currentMetafieldKey || !searchParams.has(this.currentMetafieldKey)) {
				this._cachedFilterInfo = { hasFilter: false, option: null };
				return this._cachedFilterInfo;
			}
			
			const currentValue = searchParams.get(this.currentMetafieldKey);
			const normalizedCurrentValue = currentValue ? currentValue.replace(/\+/g, ' ').replace(/%20/g, ' ') : '';
			
			const selectedOption = Array.from(this.optionsList).find(option => {
				const optionKey = option.getAttribute('data-metafield-filter-key');
				const optionValue = option.getAttribute('data-filtering-saint');
				
				if (optionKey !== this.currentMetafieldKey || !optionValue) {
					return false;
				}
				
				return normalizedCurrentValue === optionValue;
			});
			
			this._cachedFilterInfo = { hasFilter: !!selectedOption, option: selectedOption };
			return this._cachedFilterInfo;
		}
		
		this._cachedFilterInfo = { hasFilter: false, option: null };
		return this._cachedFilterInfo;
	}

	hasActiveFilter() {
		return this.getActiveFilterInfo().hasFilter;
	}

	hasSelectedOption() {
		return this.selectedOption !== null;
	}

	getSelectedOption() {
		return this.selectedOption ? this.selectedOption : null;
	}

	renderSectionFromFetch(url, signal) {
		const fetchOptions = {};
		if (signal) {
			fetchOptions.signal = signal;
		}
		
		return fetch(url, fetchOptions)
			.then(response => {
				if (signal && signal.aborted) {
					throw new DOMException('Request aborted', 'AbortError');
				}
				return response.text();
			})
			.then(responseText => {
				if (signal && signal.aborted) {
					throw new DOMException('Request aborted', 'AbortError');
				}

				const html = new DOMParser().parseFromString(responseText, 'text/html');
				const productGrid = html.getElementById('ProductGridContainer');
				const facets = html.getElementById('FacetFiltersForm');
				const productCount = html.getElementById('ProductCount');
				const productCountDesktop = html.getElementById('ProductCountDesktop');
				const CollectionTagFilters = html.getElementById('ComponentCollectionFilter')
				const activeFilterCount = html.querySelector(".mobile-facets__open")

				if (productGrid) {
					document.getElementById('ProductGridContainer').innerHTML = productGrid.innerHTML;
				}
				if (facets && document.getElementById('FacetFiltersForm')) {
					document.getElementById('FacetFiltersForm').innerHTML = facets.innerHTML;
				}
				if (CollectionTagFilters && document.getElementById('ComponentCollectionFilter')) {
					document.getElementById('ComponentCollectionFilter').outerHTML = CollectionTagFilters.outerHTML;
				}
				if (productCount) {
					const currentProductCount = document.getElementById('ProductCount');
					if (currentProductCount) {
						currentProductCount.innerHTML = productCount.innerHTML;
						currentProductCount.classList.remove('loading');
					}
				}
				if (productCountDesktop) {
					const currentProductCountDesktop = document.getElementById('ProductCountDesktop');
					if (currentProductCountDesktop) {
						currentProductCountDesktop.innerHTML = productCountDesktop.innerHTML;
						currentProductCountDesktop.classList.remove('loading');
					}
				}

				if (activeFilterCount){
					document.querySelector(".mobile-facets__open").innerHTML = activeFilterCount.innerHTML;
				}

				this.setLoadingState(false);
			})
			.catch(error => {
				if (error.name !== 'AbortError') {
					console.error('Error fetching collection data:', error);
					this.setLoadingState(false);
				}
				throw error;
			});
	}
	
	initializeSelectedValue() {
		const filterInfo = this.getActiveFilterInfo();
		
		if (filterInfo.option) {
			this.selectInitialOption(filterInfo.option);
			this.updateActionDisplay();
		}
	}
	
	getSelectedMetafieldOption() {
		const filterInfo = this.getActiveFilterInfo();
		return this.filterType === 'metafield' ? filterInfo.option : null;
	}
	
	getSelectedTagOption() {
		const filterInfo = this.getActiveFilterInfo();
		return this.filterType === 'tag' ? filterInfo.option : null;
	}
	
	selectInitialOption(option) {
		if (!option) return;
		
		const text = option.textContent.trim();
		
		this.searchInput.value = text;
		this.selectedOption = option;
		
		this.optionsList.forEach(opt => opt.classList.remove('selected'));
		option.classList.add('selected');
	}
	
	// Public method to clear all filters in this component
	clearAllFilters() {
		this.searchInput.value = '';
		this.selectedOption = null;
		this._cachedFilterInfo = null;
		this.optionsList.forEach(option => {
			option.classList.remove('selected');
		});
		this.showAllOptions();

		this.updateActionDisplay();
	}
}

customElements.define('custom-filter-select', CustomFilterSelect);